// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Sign in with Google on the web (PLAN.md P1-T02), through Google's own sign-in page (OpenID
 * Connect, response_type=id_token): no Google script runs on our pages, so our button can look
 * like the SSO one. Google sends the person back to /auth/google with an ID token in the URL
 * fragment, which the server checks (`POST /v1/auth/oidc/google`). The token is bound to this
 * attempt: Google gets hex(SHA-256(nonce)), the server gets the raw nonce, and a random state
 * ties the return to this browser. Off when the build has no client ID, and in the desktop app
 * (its origin is not a web origin Google accepts).
 */
import { isDesktop } from "./platform.js";

export const googleClientId = isDesktop ? undefined : import.meta.env.VITE_GOOGLE_CLIENT_ID;

const attemptKey = "granite.google.attempt";

/** One sign-in attempt, kept in sessionStorage until the provider sends the person back. */
export interface Attempt {
  state: string;
  nonce: string;
  /** Where to go once signed in (a path inside the app). */
  next: string;
  /** Signing up for a business account: the company's workspace to create. */
  workspaceName?: string;
}

export function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** A random nonce and its hex SHA-256, which Google (or Apple) puts in the ID token. */
export async function newNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = hex(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
  return { raw, hashed: hex(new Uint8Array(digest)) };
}

/** A random state for one attempt. */
export function newState(): string {
  return hex(crypto.getRandomValues(new Uint8Array(16)));
}

/**
 * Takes the attempt stored under key, once: it is forgotten whatever comes back, so a token
 * cannot be replayed from the callback page.
 */
export function takeAttempt(key: string): Attempt | undefined {
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return raw ? (JSON.parse(raw) as Attempt) : undefined;
  } catch {
    return undefined;
  }
}

/** Where to land after signing in: the attempt's path when it is inside the app, else /. */
export function safeNext(next: string): string {
  return next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function googleRedirectUri(): string {
  return `${window.location.origin}/auth/google`;
}

/**
 * Sends the browser to Google's sign-in page; next is where to land afterwards. With a
 * workspace name, a new account becomes a business account owning that workspace.
 */
export async function startGoogleSignIn(next: string, workspaceName?: string): Promise<void> {
  if (!googleClientId) return;
  const nonce = await newNonce();
  const attempt: Attempt = {
    state: newState(),
    nonce: nonce.raw,
    next,
    ...(workspaceName ? { workspaceName } : {}),
  };
  sessionStorage.setItem(attemptKey, JSON.stringify(attempt));
  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.search = new URLSearchParams({
    client_id: googleClientId,
    redirect_uri: googleRedirectUri(),
    response_type: "id_token",
    scope: "openid email profile",
    nonce: nonce.hashed,
    state: attempt.state,
    prompt: "select_account",
  }).toString();
  window.location.assign(url.toString());
}

export type GoogleReturn =
  | { kind: "token"; idToken: string; nonce: string; next: string; workspaceName?: string }
  | { kind: "canceled" }
  | { kind: "invalid" };

/**
 * Reads what Google sent back in the fragment, once: the attempt is forgotten, so a token
 * cannot be replayed from this page.
 */
export function takeGoogleReturn(fragment: string): GoogleReturn {
  const params = new URLSearchParams(fragment.replace(/^#/, ""));
  const attempt = takeAttempt(attemptKey);
  if (params.get("error")) return { kind: "canceled" };
  const idToken = params.get("id_token");
  if (!attempt || !idToken || params.get("state") !== attempt.state) return { kind: "invalid" };
  const next = safeNext(attempt.next);
  return { kind: "token", idToken, nonce: attempt.nonce, next, ...(attempt.workspaceName ? { workspaceName: attempt.workspaceName } : {}) };
}
